import {cn} from '@tempmee/core'
import {type TableHTMLAttributes, forwardRef, useMemo} from 'react'
import {TableContext, type TableSize} from './context'

export interface TableProps extends TableHTMLAttributes<HTMLTableElement> {
  /** Row density: `medium` (default, 56px rows) or `small` (denser). @default 'medium' */
  size?: TableSize
  /**
   * Pin the `TableHead` while the body scrolls. Requires a height-bounded,
   * scrollable ancestor (e.g. a `TableContainer` with a fixed `maxHeight`).
   * @default false
   */
  stickyHeader?: boolean
}

/**
 * Table (v3, MUI-aligned) — the root of the table family.
 *
 * Mirrors `@mui/material/Table`: renders a semantic `<table>` and provides
 * `size` / `stickyHeader` to its descendants via context. Compose with
 * `TableHead`, `TableBody`, `TableFooter`, `TableRow`, and `TableCell`. Wrap in
 * a `TableContainer` for horizontal overflow.
 */
export const Table = forwardRef<HTMLTableElement, TableProps>(
  ({className, size = 'medium', stickyHeader = false, ...props}, ref) => {
    const ctx = useMemo(() => ({size, stickyHeader}), [size, stickyHeader])

    return (
      <TableContext.Provider value={ctx}>
        <table
          ref={ref}
          className={cn(
            'w-full caption-bottom border-collapse text-left text-text-primary',
            stickyHeader && 'border-separate border-spacing-0',
            className,
          )}
          {...props}
        />
      </TableContext.Provider>
    )
  },
)
Table.displayName = 'Table'
